/**
 * AssetRecoveryPanel – makes library assets playable again, for every role.
 *
 * Lessons only play assets with a stable render URL. Many older assets have only
 * a Meshy download link, and those links expire. Step 1 re-fetches each model
 * from its Meshy task (free) and stores it permanently. Step 2 queues the ones
 * Meshy no longer has for regeneration, which spends Meshy credits, so it only
 * runs when a superadmin confirms.
 */

import { useState } from 'react';
import { toast } from 'react-toastify';
import { Loader2, RefreshCw, Sparkles, Wrench } from 'lucide-react';
import {
  meshyRegenerationService,
  type AssetRecoveryPage,
  type MeshyRegenerationItem,
} from '../../services/meshyRegenerationService';

type Totals = Omit<AssetRecoveryPage, 'nextCursor'>;
const EMPTY: Totals = { processed: 0, repaired: 0, recovered: 0, sourceExpired: 0, failed: 0, skipped: 0 };
const SCOPE = { sourceCollections: ['meshy_assets' as const], repairStatus: 'source_expired' as const };

export function AssetRecoveryPanel({ onChanged }: { onChanged?: () => void }) {
  const [running, setRunning] = useState(false);
  const [totals, setTotals] = useState<Totals | null>(null);
  const [scanning, setScanning] = useState(false);
  const [candidates, setCandidates] = useState<MeshyRegenerationItem[] | null>(null);
  const [queued, setQueued] = useState(false);

  const recover = async () => {
    setRunning(true);
    setTotals(EMPTY);
    let cursor: string | null = null;
    let sum = { ...EMPTY };
    try {
      do {
        const page = await meshyRegenerationService.recoverExpiredAssets(cursor);
        sum = {
          processed: sum.processed + page.processed,
          repaired: sum.repaired + page.repaired,
          recovered: sum.recovered + page.recovered,
          sourceExpired: sum.sourceExpired + page.sourceExpired,
          failed: sum.failed + page.failed,
          skipped: sum.skipped + page.skipped,
        };
        setTotals(sum);
        cursor = page.nextCursor;
      } while (cursor);
      toast.success(`Recovery finished: ${sum.recovered + sum.repaired} assets playable again`);
      onChanged?.();
    } catch (err: any) {
      toast.error(err?.message || 'Recovery stopped');
    } finally {
      setRunning(false);
    }
  };

  const scan = async () => {
    setScanning(true);
    try {
      const job = await meshyRegenerationService.createJob({ dryRun: true, scope: SCOPE });
      setCandidates(job.items || []);
    } catch (err: any) {
      toast.error(err?.message || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const startRegeneration = async () => {
    if (!candidates?.length) return;
    setScanning(true);
    try {
      await meshyRegenerationService.createJob({
        dryRun: false,
        scope: SCOPE,
        selectedItems: candidates.map((item) => ({
          sourceCollection: item.source_collection,
          sourceAssetId: item.source_asset_id,
        })),
      });
      setQueued(true);
      toast.success(`Regeneration queued for ${candidates.length} assets`);
    } catch (err: any) {
      toast.error(err?.message || 'Could not queue regeneration');
    } finally {
      setScanning(false);
    }
  };

  const stat = (label: string, value: number, tone = 'text-foreground') => (
    <div className="rounded-md border border-border bg-background/60 px-3 py-2">
      <div className={`text-lg font-semibold tabular-nums ${tone}`}>{value}</div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </div>
  );

  return (
    <div className="mb-6 rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Wrench className="h-4 w-4 text-primary" />
            Asset health
          </h2>
          <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
            Lessons can only play assets stored in In3D.ai. Recover re-downloads models whose Meshy links
            have expired and stores them permanently, so they appear for teachers, students and every role.
          </p>
        </div>
        <button
          type="button"
          onClick={recover}
          disabled={running}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {running ? 'Recovering…' : 'Recover expired assets'}
        </button>
      </div>

      {totals && (
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {stat('Checked', totals.processed)}
          {stat('Recovered from Meshy', totals.recovered, 'text-emerald-400')}
          {stat('Storage links fixed', totals.repaired, 'text-emerald-400')}
          {stat('Already fine / retired', totals.skipped, 'text-muted-foreground')}
          {stat('No longer on Meshy', totals.sourceExpired, 'text-amber-400')}
          {stat('Failed', totals.failed, totals.failed ? 'text-rose-400' : 'text-muted-foreground')}
        </div>
      )}

      {totals && !running && totals.sourceExpired > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs">
          <span className="text-amber-200">
            {candidates === null
              ? `${totals.sourceExpired} assets are gone from Meshy and must be regenerated from their prompts.`
              : `${candidates.length} can be regenerated (about ${candidates.length * 2} Meshy tasks). ` +
                `${Math.max(totals.sourceExpired - candidates.length, 0)} have no prompt or lesson and need relinking by hand.`}
          </span>
          {candidates === null ? (
            <button type="button" onClick={scan} disabled={scanning}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 font-medium text-foreground hover:bg-muted disabled:opacity-60">
              {scanning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Check what can be regenerated
            </button>
          ) : (
            <button type="button" onClick={startRegeneration} disabled={scanning || queued || candidates.length === 0}
              className="inline-flex items-center gap-1.5 rounded-md bg-amber-500 px-2.5 py-1.5 font-medium text-black hover:bg-amber-400 disabled:opacity-60">
              {scanning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {queued ? 'Regeneration queued' : `Start regeneration (${candidates.length})`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
